import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

/** Conditional class names with Tailwind conflict resolution. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/**
 * Case-insensitive subsequence match — the same behaviour editors use for
 * "go to file". Returns the matched character indices so the UI can highlight
 * them, or `null` when the needle does not fit.
 */
export function fuzzyMatch(haystack: string, needle: string): number[] | null {
  if (needle.length === 0) return []
  const source = haystack.toLowerCase()
  const query = needle.toLowerCase()
  const indices: number[] = []
  let cursor = 0

  for (const character of query) {
    const found = source.indexOf(character, cursor)
    if (found === -1) return null
    indices.push(found)
    cursor = found + 1
  }

  return indices
}

/**
 * Lower is better. Rewards prefix matches and tight, contiguous runs so
 * "aud" ranks the audit log above "Broadcasts".
 */
export function fuzzyScore(haystack: string, indices: number[]): number {
  if (indices.length === 0) return 0
  const first = indices[0] ?? 0
  const last = indices[indices.length - 1] ?? 0
  const spread = last - first
  const wordStartBonus = first === 0 || /[\s\-/]/.test(haystack[first - 1] ?? '') ? -20 : 0
  return first + spread + wordStartBonus
}

export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

let idCounter = 0
/** Stable-enough unique id for aria wiring in components without a key prop. */
export function nextId(prefix: string): string {
  idCounter += 1
  return `${prefix}-${idCounter}`
}
